import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  MapPin,
  Star,
  Store,
  Search,
  ArrowRight,
  Navigation,
  Scissors,
  LayoutGrid,
  List,
  X,
} from 'lucide-react';
import { useVerifiedShops } from '../../api';
import { useServicesLocation } from '../../hooks/useServicesLocation';
import { getShopLatLng, distanceKm as haversineKm, formatDistanceKm } from '../../utils/geo';
import { shopMatchesSelectedCity } from '../../utils/locationPreference';
import { formatShopAddress } from '../../utils/shopAddress';
import styles from './salonList.module.css';

const SalonsPage = () => {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';

  const { data: salons = [], isLoading: loadingSalons } = useVerifiedShops();
  const { userPos, selectedCityId, selectedCityName, openLocationModal } = useServicesLocation();

  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [sortBy, setSortBy] = useState('rating');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'

  useEffect(() => {
    if (initialSearch) setSearchTerm(initialSearch);
  }, [initialSearch]);

  useEffect(() => {
    if (sortBy === 'distance' && !userPos) {
      setSortBy('rating');
    }
  }, [sortBy, userPos]);

  const salonsWithMeta = useMemo(() => {
    return salons.map((salon) => {
      const coords = getShopLatLng(salon);
      let dist = null;
      if (userPos && coords) {
        dist = haversineKm(userPos.lat, userPos.lng, coords.lat, coords.lng);
      }
      return {
        ...salon,
        _distanceKm: dist,
      };
    });
  }, [salons, userPos]);

  const filteredSalons = useMemo(() => {
    return salonsWithMeta
      .filter((salon) => {
        if (selectedCityId && !shopMatchesSelectedCity(salon, selectedCityId)) {
          return false;
        }
        const addr = formatShopAddress(salon).toLowerCase();
        const matchesSearch =
          salon.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          addr.includes(searchTerm.toLowerCase());
        return matchesSearch;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'distance': {
            const da = a._distanceKm ?? Infinity;
            const db = b._distanceKm ?? Infinity;
            return da - db;
          }
          case 'rating':
            return (b.rating || 0) - (a.rating || 0);
          case 'name':
            return a.name.localeCompare(b.name);
          default:
            return 0;
        }
      });
  }, [salonsWithMeta, searchTerm, sortBy, selectedCityId]);

  return (
    <div className={styles.page}>
      {/* Compact Hero Bar */}
      <div className={styles.compactHero}>
        <div className={styles.heroLeft}>
          <div className={styles.heroIconBox}>
            <Store size={20} />
          </div>
          <div>
            <h1 className={styles.heroTitle}>Barbershops &amp; Salons</h1>
            <p className={styles.heroSubtitle}>
              Find verified salons and book instant chair appointments
            </p>
          </div>
        </div>

        <div className={styles.heroRight}>
          <button
            type="button"
            className={styles.heroLocationBtn}
            onClick={openLocationModal}
            title="Filter by city"
          >
            <MapPin size={14} style={{ color: '#f87171' }} />
            <span>{selectedCityName || 'All Cities'}</span>
          </button>
          <span className={styles.heroCountBadge}>{filteredSalons.length} Available</span>
        </div>
      </div>

      {/* Unified Toolbar */}
      <div className={styles.toolbarCard}>
        <div className={styles.searchWrap}>
          <Search size={15} style={{ color: '#94a3b8' }} />
          <input
            type="text"
            placeholder="Search by salon name, area, or street…"
            className={styles.searchInput}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              className={styles.clearSearchBtn}
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className={styles.toolbarControls}>
          <select
            className={styles.sortSelect}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="rating">Sort: Top Rated ★</option>
            {userPos && <option value="distance">Sort: Nearest</option>}
            <option value="name">Sort: Name (A–Z)</option>
          </select>

          <div className={styles.viewToggleGroup}>
            <button
              type="button"
              className={`${styles.viewToggleBtn} ${viewMode === 'grid' ? styles.viewToggleBtnActive : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <LayoutGrid size={15} />
            </button>
            <button
              type="button"
              className={`${styles.viewToggleBtn} ${viewMode === 'list' ? styles.viewToggleBtnActive : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <List size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Results Status Bar */}
      <div className={styles.statusBar}>
        <span>
          Showing {filteredSalons.length}{' '}
          {filteredSalons.length === 1 ? 'barbershop' : 'barbershops'}
          {selectedCityName ? ` in ${selectedCityName}` : ''}
        </span>
      </div>

      {/* Salons Grid / List Rendering */}
      {loadingSalons ? (
        <div className={styles.emptyState}>
          <Scissors
            size={28}
            style={{ color: '#dc2626', margin: '0 auto 8px', animation: 'pulse 1.5s infinite' }}
          />
          <div className={styles.emptyTitle}>Finding verified salons…</div>
          <p className={styles.emptyText}>Fetching live shop directories and schedules.</p>
        </div>
      ) : filteredSalons.length > 0 ? (
        viewMode === 'grid' ? (
          <div className={styles.compactGrid}>
            {filteredSalons.map((salon) => {
              const coverImage = salon.images?.[0]?.url || salon.images?.[0];
              const addr = formatShopAddress(salon) || 'Salon Location';
              const rating = salon.rating ? Number(salon.rating).toFixed(1) : '4.8';
              const offeringsCount = salon.products?.length || 0;
              const distanceText =
                salon._distanceKm != null ? formatDistanceKm(salon._distanceKm) : null;

              return (
                <Link key={salon._id} to={`/salon/${salon._id}`} className={styles.compactCard}>
                  <div className={styles.compactMedia}>
                    {coverImage ? (
                      <img src={coverImage} alt={salon.name} className={styles.compactImg} />
                    ) : (
                      <div className={styles.placeholder}>
                        <Store size={32} />
                      </div>
                    )}

                    <span className={styles.ratingPill}>
                      <Star size={11} fill="#fbbf24" stroke="#fbbf24" />
                      <span>{rating}</span>
                    </span>

                    {salon.verified && <span className={styles.verifiedBadge}>Verified</span>}

                    {distanceText && (
                      <span className={styles.distanceTag}>
                        <Navigation size={9} />
                        <span>{distanceText}</span>
                      </span>
                    )}
                  </div>

                  <div className={styles.compactBody}>
                    <h3 className={styles.compactName} title={salon.name}>
                      {salon.name}
                    </h3>

                    <div className={styles.compactAddress} title={addr}>
                      <MapPin
                        size={12}
                        style={{ color: '#dc2626', flexShrink: 0, marginTop: '1px' }}
                      />
                      <span>{addr}</span>
                    </div>

                    <div className={styles.compactFooter}>
                      <span className={styles.servicesCount}>{offeringsCount} Services</span>

                      <div className={styles.btnBook}>
                        <span>Book Slot</span>
                        <ArrowRight size={12} />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className={styles.compactList}>
            {filteredSalons.map((salon) => {
              const coverImage = salon.images?.[0]?.url || salon.images?.[0];
              const addr = formatShopAddress(salon) || 'Salon Location';
              const rating = salon.rating ? Number(salon.rating).toFixed(1) : '4.8';
              const offeringsCount = salon.products?.length || 0;
              const distanceText =
                salon._distanceKm != null ? formatDistanceKm(salon._distanceKm) : null;

              return (
                <Link key={salon._id} to={`/salon/${salon._id}`} className={styles.listRow}>
                  <div className={styles.listLeft}>
                    <div className={styles.listThumbnail}>
                      {coverImage ? (
                        <img src={coverImage} alt={salon.name} className={styles.listImg} />
                      ) : (
                        <div className={styles.placeholder}>
                          <Store size={26} />
                        </div>
                      )}
                    </div>
                    <div className={styles.listContent}>
                      <div className={styles.listTitleRow}>
                        <h3 className={styles.listTitle}>{salon.name}</h3>
                        <span className={styles.listRatingBadge}>
                          <Star size={10} fill="#d97706" stroke="#d97706" />
                          <span>{rating}</span>
                        </span>
                        {salon.verified && (
                          <span style={{ fontSize: '0.675rem', color: '#16a34a', fontWeight: 700 }}>
                            ✓ Verified
                          </span>
                        )}
                      </div>
                      <div className={styles.listMeta}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                          <MapPin size={11} style={{ color: '#dc2626' }} />
                          <span>{addr}</span>
                        </span>
                        {distanceText && <span style={{ color: '#94a3b8' }}>· {distanceText}</span>}
                        <span style={{ color: '#94a3b8' }}>· {offeringsCount} services</span>
                      </div>
                    </div>
                  </div>

                  <div className={styles.listRight}>
                    <div className={styles.btnBook}>
                      <span>Book Slot</span>
                      <ArrowRight size={12} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )
      ) : (
        <div className={styles.emptyState}>
          <Store size={36} style={{ color: '#94a3b8', margin: '0 auto 8px' }} />
          <div className={styles.emptyTitle}>No Salons Found</div>
          <p className={styles.emptyText}>
            No salons match your search criteria in this location. Try changing your city filter or
            search keyword.
          </p>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button
              type="button"
              className={styles.cityFilterBtn}
              onClick={() => setSearchTerm('')}
            >
              Clear Search
            </button>
            <button type="button" className={styles.btnBook} onClick={openLocationModal}>
              Change Location
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalonsPage;
